import _mapInstanceProperty from "@babel/runtime-corejs3/core-js/instance/map";
import _findInstanceProperty from "@babel/runtime-corejs3/core-js/instance/find";
import vSelectField from "@Shared/components/form-elements/fields/v-select-field.vue";
import * as ReferrerActions from "../../referrers/store/referrer-actions";
import * as ReferrerMutations from "../../referrers/store/referrer-mutations";
import Amplitude from "../../../../../../shared/utils/amplitude";
import { V_SELECT_SCENARIO } from "@Shared/js/constants";
import InlineLoader from "@Shared/components/general/inline-loader.vue";
import { computed, defineComponent, onMounted, ref, toRefs, nextTick } from "@vue/composition-api";
import * as ReferrerGetters from "../store/referrer-getters";
import { useReferrerStoreActions, useReferrerStoreGetters, useReferrerStoreMutations } from "../store/referrer-helpers";
import { debounce } from "lodash";
export default defineComponent({
  name: "ReferrersSelect",
  components: {
    vSelectField,
    InlineLoader
  },
  props: {
    multiple: {
      type: Boolean,
      default: false
    },
    required: {
      type: Boolean,
      default: null
    },
    value: {
      type: [String, Array],
      default: ""
    },
    spaceClasses: {
      type: String,
      default: "col-12 mt-3 pb-4 px-0"
    },
    allowCreate: {
      type: Boolean,
      default: true
    },
    allowClear: {
      type: Boolean,
      default: true
    },
    hasCheckboxes: {
      type: Boolean,
      default: false
    },
    label: {
      type: String,
      default: null
    },
    placeholder: {
      type: String,
      default: ""
    },
    noLabel: {
      type: Boolean,
      default: false
    },
    extraSelectData: {
      type: Array,
      default: () => []
    },
    defaultReferrers: {
      type: [Array, String],
      required: false,
      default: () => ""
    },
    action: {
      type: String,
      default: ""
    }
  },
  setup(props, {
    emit
  }) {
    const {
      value,
      extraSelectData,
      multiple,
      defaultReferrers,
      action
    } = toRefs(props);
    const firstTimeLoading = ref(false);
    const select = ref(null);
    const loadingStates = ref({
      loadingReferrers: false,
      searchingReferrers: false,
      loadingMoreReferrers: false
    });
    const referrerSearchString = ref("");
    const searchOffset = ref(0);
    const limit = ref(20);
    const defaultValue = ref(null);
    const {
      RAW_REFERRERS,
      REFERRERS_COUNT,
      REFERRERS_LOADED
    } = useReferrerStoreGetters([ReferrerGetters.RAW_REFERRERS, ReferrerGetters.REFERRERS_COUNT, ReferrerGetters.REFERRERS_LOADED]);
    const {
      LOAD_REFERRERS
    } = useReferrerStoreActions([ReferrerActions.LOAD_REFERRERS]);
    const {
      RESET_REFERRERS
    } = useReferrerStoreMutations([ReferrerMutations.RESET_REFERRERS]);
    const actionRef = ref(action);
    const allReferrersLoaded = computed(() => REFERRERS_LOADED.value && REFERRERS_LOADED.value >= REFERRERS_COUNT.value);
    const scenario = computed(() => V_SELECT_SCENARIO.referrers);
    const referrers = computed(() => {
      if (!RAW_REFERRERS.value.length) {
        return [...RAW_REFERRERS.value];
      } else return [...extraSelectData.value, ...RAW_REFERRERS.value];
    });
    const defaultValueComputed = computed(() => {
      if (Array.isArray(defaultReferrers.value) && defaultReferrers.value.length != 0) {
        var _context;
        return _mapInstanceProperty(_context = defaultReferrers.value).call(_context, dr => {
          var _context2;
          return _findInstanceProperty(_context2 = RAW_REFERRERS.value).call(_context2, rr => rr.id == dr);
        });
      }
      if (extraSelectData.value.length != 0) {
        return [...extraSelectData.value];
      }
      if (!value.value) {
        return [];
      }
      return defaultValue.value;
    });
    onMounted(async () => {
      await nextTick();
      firstTimeLoading.value = true;
      loadingStates.value.loadingReferrers = true;
      await loadReferrers();
      firstTimeLoading.value = false;
      loadingStates.value.loadingReferrers = false;
      if (value.value) {
        var _context3;
        const referrerFromList = _findInstanceProperty(_context3 = RAW_REFERRERS.value).call(_context3, r => r.id == value.value);
        defaultValue.value = referrerFromList;
        emit("set-existing-referrer", referrerFromList);
      }
    });
    const loadReferrers = async (loadingKey = "loadingReferrers") => {
      loadingStates.value[loadingKey] = true;
      switch (loadingKey) {
        case "loadingReferrers":
          {
            actionRef.value = "";
            RESET_REFERRERS();
            if (value.value) {
              await LOAD_REFERRERS({
                searchString: value.value,
                orderBy: "",
                orderType: "",
                concatenate: true
              });
            }
            await LOAD_REFERRERS({
              searchString: "",
              orderBy: "",
              orderType: "",
              concatenate: true
            });
            break;
          }
        case "loadingMoreReferrers":
          {
            if (allReferrersLoaded.value) {
              loadingStates.value[loadingKey] = false;
              return;
            }
            await LOAD_REFERRERS({
              searchString: "",
              offset: REFERRERS_LOADED.value,
              limit: limit.value
            });
            break;
          }
        case "searchingReferrers":
          {
            await LOAD_REFERRERS({
              searchString: referrerSearchString.value,
              offset: referrerSearchString.value ? searchOffset.value : REFERRERS_LOADED.value,
              limit: limit.value,
              concatenate: referrerSearchString.value ? true : REFERRERS_LOADED.value
            });
          }
      }
      loadingStates.value[loadingKey] = false;
    };
    const handleChange = (data, type) => {
      emit("change", data, type);
      if (!multiple.value) loadReferrers();
      logHandleChange(data, type);
    };
    const handleDeselect = (data, type) => {
      emit("change", data, type);
      logHandleChange(data, type);
    };
    const handleClearReferrer = () => {
      emit("change", null, "unselect");
    };
    const handleSearchReferrer = debounce(search => {
      if (search != "") {
        referrerSearchString.value = search;
        searchOffset.value = 0;
        RESET_REFERRERS();
        loadReferrers("searchingReferrers");
      } else {
        loadReferrers("loadingReferrers");
      }
    }, 2000);
    const loadMoreReferrers = () => {
      loadReferrers("loadingMoreReferrers");
    };
    const logHandleChange = (data, type) => {
      var _data$username;
      const eventName = "Referrers Select -";
      Amplitude.logEvent(`${eventName} ${type}`, {
        data: {
          id: (_data$username = data == null ? void 0 : data.username) != null ? _data$username : data.id
        },
        action: type
      });
    };
    return {
      referrers,
      referrerSearchString,
      allReferrersLoaded,
      firstTimeLoading,
      select,
      loadingStates,
      scenario,
      handleChange,
      handleDeselect,
      handleClearReferrer,
      handleSearchReferrer,
      loadMoreReferrers,
      defaultValueComputed,
      actionRef
    };
  }
});